/**
 * SalaryTools Admin Dashboard - React version
 * Replaces the serverless function with same functionality
 * Access: /admin
 */
import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Box, TextField, Button, Alert, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import { makeMeta } from '../utils/seo';

// Generate dummy analytics data for local development
const generateDummyAnalytics = () => {
  const paths = ['/', '/salary-calculator', '/about', '/salary/50000', '/salary/75000'];
  const countries = ['GB', 'US', 'FR', 'DE', 'CA'];
  const pageviews = [];
  
  const now = Date.now();
  for (let i = 0; i < 50; i++) {
    const hoursAgo = Math.floor(Math.random() * 24 * 7); // Last 7 days
    const timestamp = new Date(now - (hoursAgo * 60 * 60 * 1000));
    
    pageviews.push({
      path: paths[Math.floor(Math.random() * paths.length)],
      timestamp: timestamp.toISOString(),
      country: countries[Math.floor(Math.random() * countries.length)],
      user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      ip_hash: `hash_${Math.random().toString(36).substring(2, 10)}`,
      referer: Math.random() > 0.7 ? 'https://www.google.com/' : null
    });
  }
  
  // Sort by timestamp (most recent first)
  pageviews.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  
  // Generate summary
  const pathCounts = {};
  const countryCounts = {};
  const dailyCounts = {};
  
  pageviews.forEach(pv => {
    pathCounts[pv.path] = (pathCounts[pv.path] || 0) + 1;
    countryCounts[pv.country] = (countryCounts[pv.country] || 0) + 1;
    const day = pv.timestamp.split('T')[0];
    dailyCounts[day] = (dailyCounts[day] || 0) + 1;
  });

  return {
    total_pageviews: pageviews.length,
    pageviews: pageviews, // Dashboard expects this key
    recent_pageviews: pageviews, // Keep for backward compatibility
    oldest_entry: pageviews[pageviews.length - 1]?.timestamp,
    newest_entry: pageviews[0]?.timestamp,
    keys_scanned: pageviews.length,
    days_requested: 7, // Add for dummy data
    summary: {
      total_pageviews: pageviews.length,
      unique_paths: Object.keys(pathCounts).length,
      top_pages: Object.entries(pathCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([path, count]) => ({ path, count })),
      countries: Object.entries(countryCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([country, count]) => ({ country, count })),
      daily_counts: dailyCounts
    },
    environment: process.env.NODE_ENV === 'development' ? 'local-dummy-data' : 'production'
  };
};

// Fetch analytics data from the serverless endpoint with authentication
const fetchAnalyticsData = async () => {
  try {
    // In development, always use dummy data since we don't have the serverless functions
    if (process.env.NODE_ENV === 'development') {
      console.log('Development mode: using dummy analytics data');
      return generateDummyAnalytics();
    }
    
    // In production, authenticate with the analytics API using session token
    const sessionToken = sessionStorage.getItem('admin-session');
    if (!sessionToken) {
      throw new Error('Session token not available - please re-authenticate');
    }
    
    const response = await fetch('/api/analytics?days=30&limit=100', {
      headers: {
        'Authorization': `Bearer ${sessionToken}`
      }
    });
    
    console.log('Analytics API response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Analytics API error response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Analytics API data:', data);
    
    // Handle error response from API
    if (data.error) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Analytics API returned error, using dummy data');
        return generateDummyAnalytics();
      }
      
      return {
        error: data.error,
        details: data.details || 'No additional details'
      };
    }
    
    // Transform to match the dashboard format
    return {
      total_pageviews: data.total_count || data.pageviews?.length || 0,
      pageviews: data.pageviews || [], // Dashboard expects this key
      recent_pageviews: data.pageviews || [], // Keep for backward compatibility
      oldest_entry: data.pageviews?.[data.pageviews.length - 1]?.timestamp || null,
      newest_entry: data.pageviews?.[0]?.timestamp || null,
      keys_scanned: data.total_count || data.pageviews?.length || 0,
      summary: data.summary,
      environment: 'production',
      days_requested: 30 // Add this for the summary cards
    };
  } catch (error) {
    console.error('Analytics fetch error:', error);
    
    // Fallback to dummy data in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Using dummy data due to fetch error');
      return generateDummyAnalytics();
    }
    
    return { 
      error: 'Failed to fetch analytics data', 
      details: error.message 
    };
  }
};

const Admin = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Validate existing session on load
  useEffect(() => {
    const validateSession = async () => {
      const sessionToken = sessionStorage.getItem('admin-session');
      if (!sessionToken) return;
      
      try {
        // In development, just check if token exists
        if (process.env.NODE_ENV === 'development') {
          setIsAuthenticated(true);
          return;
        }
        
        // In production, validate with server
        const response = await fetch('/api/admin-validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionToken })
        });
        
        const result = await response.json();
        if (result.valid) {
          setIsAuthenticated(true);
        } else {
          // Clear invalid session
          sessionStorage.removeItem('admin-session');
          sessionStorage.removeItem('admin-authenticated');
        }
      } catch (error) {
        console.error('Session validation failed:', error);
        sessionStorage.removeItem('admin-session');
        sessionStorage.removeItem('admin-authenticated');
      }
    };
    
    validateSession();
  }, []);

  // Hash password client-side for security
  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Basic authentication with client-side password hashing
  const handleAuth = async () => {
    if (!password) {
      setError('Password required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // In development, use simple password
      if (process.env.NODE_ENV === 'development') {
        if (password === 'dev123') {
          setIsAuthenticated(true);
          sessionStorage.setItem('admin-authenticated', 'true');
          setLoading(false);
          return;
        } else {
          setError('Invalid password. Use: dev123');
          setLoading(false);
          return;
        }
      }

      // Hash password before sending to server
      const hashedPassword = await hashPassword(password);
      
      // In production, validate with serverless auth endpoint
      const response = await fetch('/api/admin-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          passwordHash: hashedPassword,
          timestamp: Date.now() // Prevent replay attacks
        })
      });

      const result = await response.json();

      if (result.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin-authenticated', 'true');
        sessionStorage.setItem('admin-session', result.sessionToken);
        // DO NOT store password/credentials in browser for security
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Authentication failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    setError(null);
    setLoading(true);

    try {
      const data = await fetchAnalyticsData();
      setAnalyticsData(data);
    } catch (err) {
      setError(err.message);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAnalyticsData();
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin-authenticated');
    sessionStorage.removeItem('admin-session');
    setAnalyticsData(null);
    setPassword('');
  };

  // Login screen (matches serverless basic auth UX)
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        {makeMeta({
          title: 'Admin Dashboard',
          description: 'SalaryTools admin dashboard - authentication required'
        })}
        
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            SalaryTools Admin
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Authentication required
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ mt: 3 }}>
            <TextField
              type="password"
              label="Admin Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
              fullWidth
              margin="normal"
              autoFocus
            />
            <Button 
              variant="contained" 
              onClick={handleAuth}
              disabled={!password || loading}
              fullWidth
              sx={{ mt: 2 }}
            >
              {loading ? 'Authenticating...' : 'Login'}
            </Button>
            
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                Dev password: dev123
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>
    );
  }

  // Main admin dashboard (matches serverless admin layout)
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {makeMeta({
        title: 'Admin Dashboard',
        description: 'SalaryTools admin dashboard - analytics and metrics'
      })}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h3" component="h1">
          SalaryTools Admin Dashboard
        </Typography>
        <Button variant="outlined" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
      
      {process.env.NODE_ENV === 'development' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Development Mode:</strong> Authentication is simplified for local development. 
          Production uses secure session validation with rate limiting and CSRF protection.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Button 
          variant="outlined" 
          onClick={loadAnalyticsData}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {/* Analytics Dashboard */}
      {analyticsData && !analyticsData.error ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Pageviews
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.summary?.total_pageviews?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Last {analyticsData.days_requested || 7} days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Unique Pages
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.summary?.unique_paths || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Different URLs visited
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Countries
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.summary?.countries?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Visitor locations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Avg Daily Views
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.summary?.total_pageviews && analyticsData.days_requested 
                      ? Math.round(analyticsData.summary.total_pageviews / analyticsData.days_requested)
                      : 0
                    }
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Views per day
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Pages and Countries */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Pages
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Page</TableCell>
                        <TableCell align="right">Views</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.summary?.top_pages?.slice(0, 10).map((page, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {page.path}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={page.count} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Countries
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Country</TableCell>
                        <TableCell align="right">Views</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.summary?.countries?.slice(0, 10).map((country, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {country.country}
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={country.count} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Recent Pageviews */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Pageviews
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Page</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Referrer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData.pageviews?.slice(0, 20).map((pageview, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(pageview.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {pageview.path}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={pageview.country} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {pageview.referer || 'Direct'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Debug Info (Collapsible) */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Debug Information
            </Typography>
            <Box 
              component="pre" 
              sx={{ 
                bgcolor: '#f9f9f9', 
                p: 2, 
                overflow: 'auto',
                fontSize: '0.8rem',
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                maxHeight: 300
              }}
            >
              {JSON.stringify({
                environment: analyticsData.environment,
                total_count: analyticsData.total_count,
                days_requested: analyticsData.days_requested,
                dates_processed: analyticsData.dates_processed,
                cutoff_timestamp: analyticsData.cutoff_timestamp,
                oldest_entry: analyticsData.oldest_entry,
                newest_entry: analyticsData.newest_entry
              }, null, 2)}
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 2, 
                fontStyle: 'italic'
              }}
            >
              Last updated: {new Date().toISOString()}
            </Typography>
          </Paper>
        </>
      ) : analyticsData?.error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Error:</strong> {analyticsData.error}
          {analyticsData.details && (
            <Box component="div" sx={{ mt: 1 }}>
              <strong>Details:</strong> {analyticsData.details}
            </Box>
          )}
        </Alert>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography>Loading analytics data...</Typography>
        </Paper>
      )}
    </Container>
  );
};

export default Admin;